'use client';

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { createPortal } from 'react-dom';
import Link from 'next/link';
import { Search, X } from 'lucide-react';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { generateAvatarFallback } from '@/utils/generateAvatarFallback';
import { LoadingSpinner, BlueCheck, GoldCheck } from '@/components/Icons';
import useFetchBrokers from '@/components/(postTypes)/platforms/(single)/utils/useFetchHeader';
import { Broker } from '@/types/exchanges/single';

interface PlatformSearchModalProps {
    isOpen: boolean;
    onClose: () => void;
    categories?: 'foreign' | 'iranian' | 'props' | 'broker' | 'gold' | undefined;
    contenttype?: string;
    currentUri?: string;
    currentName?: string;
}

// Persian/Arabic and English text normalization for fuzzy and accurate search
export const normalizeSearchText = (str = ''): string => {
    return str
        .toLowerCase()
        .replace(/[آأإ]/g, 'ا')
        .replace(/[ي]/g, 'ی')
        .replace(/[ك]/g, 'ک')
        .replace(/[ة]/g, 'ه')
        .replace(/[\u200c\u200b\u00ad]/g, '')
        .replace(/\s+/g, ' ')
        .trim();
};

export const PlatformSearchModal: React.FC<PlatformSearchModalProps> = ({
    isOpen,
    onClose,
    categories = 'broker',
    contenttype,
    currentUri,
    currentName,
}) => {
    const [searchTerm, setSearchTerm] = useState<string>('');
    const [isMounted, setIsMounted] = useState<boolean>(false);

    useEffect(() => {
        setIsMounted(true);
    }, []);

    const handleClose = useCallback(
        (e?: React.MouseEvent | React.TouchEvent) => {
            if (e) {
                e.stopPropagation();
            }
            // Dismiss virtual keyboard cleanly before closing
            if (typeof document !== 'undefined' && document.activeElement instanceof HTMLElement) {
                document.activeElement.blur();
            }
            onClose();
        },
        [onClose],
    );

    // Body scroll lock and Escape key handling
    useEffect(() => {
        if (!isOpen) return;

        const originalOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';

        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                handleClose();
            }
        };

        window.addEventListener('keydown', handleKeyDown);

        return () => {
            document.body.style.overflow = originalOverflow;
            window.removeEventListener('keydown', handleKeyDown);
        };
    }, [isOpen, handleClose]);

    // Reset search term when closed
    useEffect(() => {
        if (!isOpen) {
            setSearchTerm('');
        }
    }, [isOpen]);

    // Fetch platforms catalog for the category (search is handled client-side instantly)
    const { brokers, isLoading, error, pageInfo, fetchMoreBrokers } = useFetchBrokers(
        {
            categories,
            searchTerm: '',
        },
        { enabled: isOpen },
    );

    // Instant local filtering with Persian normalization for zero-latency, 100% accurate results
    const displayedBrokers = useMemo(() => {
        if (!searchTerm.trim()) return brokers;
        const normTerm = normalizeSearchText(searchTerm);
        if (!normTerm) return brokers;

        return brokers.filter((b) => {
            const fa = normalizeSearchText(b.faName || '');
            const en = (b.enName || '').toLowerCase();
            const uri = (b.uri || '').toLowerCase();
            return fa.includes(normTerm) || en.includes(normTerm) || uri.includes(normTerm);
        });
    }, [brokers, searchTerm]);

    const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setSearchTerm(e.target.value);
    };

    const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
        const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
        if (scrollHeight - scrollTop <= clientHeight + 50 && pageInfo.hasMore && !isLoading) {
            fetchMoreBrokers();
        }
    };

    const getModalTitle = () => {
        if (contenttype) return `انتخاب و جستجوی ${contenttype}`;
        switch (categories) {
            case 'broker':
                return 'انتخاب و جستجوی بروکر';
            case 'foreign':
                return 'انتخاب و جستجوی صرافی خارجی';
            case 'iranian':
                return 'انتخاب و جستجوی صرافی ایرانی';
            case 'props':
                return 'انتخاب و جستجوی پراپ تریدینگ';
            case 'gold':
                return 'انتخاب و جستجوی سایت طلا';
            default:
                return 'انتخاب و جستجوی پلتفرم';
        }
    };

    const getPlaceholder = () => {
        switch (categories) {
            case 'broker':
                return 'جستجوی نام بروکر...';
            case 'foreign':
            case 'iranian':
                return 'جستجوی نام صرافی...';
            case 'props':
                return 'جستجوی نام پراپ...';
            case 'gold':
                return 'جستجوی نام سایت طلا...';
            default:
                return 'جستجوی نام...';
        }
    };

    if (!isOpen || !isMounted || typeof document === 'undefined') return null;

    const modalMarkup = (
        <div
            className='fixed inset-0 z-[9999] flex items-end sm:items-center justify-center bg-black/60 transition-opacity duration-150'
            onClick={handleClose}
        >
            {/* Desktop View: Centered Dialog | Mobile View: Bottom Drawer */}
            <div
                className='w-full sm:max-w-md bg-white dark:bg-primary-active border-t sm:border border-gray-100 dark:border-white/15 rounded-t-3xl sm:rounded-2xl shadow-2xl overflow-hidden animate-in slide-in-from-bottom sm:zoom-in-95 duration-200 flex flex-col h-[75dvh] sm:h-[500px]'
                onClick={(e) => e.stopPropagation()}
            >
                {/* Modal Header */}
                <div className='flex items-center justify-between p-5 pb-4 border-b border-gray-100 dark:border-white/10 shrink-0'>
                    <p className='font-bold text-sm lg:text-base text-gray-900 dark:text-white'>
                        {getModalTitle()}
                    </p>
                    <button
                        type='button'
                        onClick={handleClose}
                        aria-label='بستن'
                        className='p-2 -m-1 rounded-xl text-gray-400 hover:text-gray-600 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-white/10 active:scale-95 transition-all cursor-pointer'
                    >
                        <X className='size-5' />
                    </button>
                </div>

                {/* Search Bar */}
                <div className='p-3 border-b border-gray-100 dark:border-white/5 bg-gray-50/50 dark:bg-white/5 shrink-0'>
                    <div className='relative'>
                        <Search className='absolute right-3 top-1/2 -translate-y-1/2 size-4 text-gray-400 dark:text-gray-500' />
                        <input
                            type='text'
                            placeholder={getPlaceholder()}
                            value={searchTerm}
                            onChange={handleSearchChange}
                            className='w-full pr-9 pl-8 py-2.5 text-base placeholder:text-sm bg-white dark:bg-primary-dark border border-gray-200 dark:border-white/10 rounded-xl text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:border-blue-500 dark:focus:border-blue-400 shadow-xs transition-colors'
                        />
                        {searchTerm && (
                            <button
                                type='button'
                                onClick={() => setSearchTerm('')}
                                className='absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 p-1'
                            >
                                <X className='size-3.5' />
                            </button>
                        )}
                    </div>
                </div>

                {/* Scrollable List with fixed flex-1 container */}
                <div
                    onScroll={handleScroll}
                    className='flex-1 min-h-0 overflow-y-auto p-2 space-y-1 no-scrollbar'
                >
                    {displayedBrokers.map((item: Broker, index: number) => {
                        const isCurrent =
                            (currentUri && item.uri && item.uri.trim() === currentUri.trim()) ||
                            (currentName &&
                                item.faName &&
                                item.faName.trim().toLowerCase() ===
                                    currentName.trim().toLowerCase());

                        const displayName = item.faName || item.enName || '';

                        return (
                            <Link
                                key={`${item.uri || item.faName || index}-${index}`}
                                href={item.uri || '#'}
                                onClick={onClose}
                                className={`flex items-center justify-between p-2.5 sm:p-2.5 rounded-xl text-xs sm:text-sm cursor-pointer transition-all duration-150 active:bg-gray-200 dark:active:bg-white/10 ${
                                    isCurrent
                                        ? 'bg-blue-50 dark:bg-blue-600/20 text-blue-600 dark:text-blue-400 font-bold'
                                        : 'hover:bg-gray-100 dark:hover:bg-white/5 text-gray-700 dark:text-gray-200'
                                }`}
                            >
                                <div className='flex items-center gap-3 overflow-hidden'>
                                    <Avatar className='size-8 sm:size-9 flex-shrink-0 border border-gray-100 dark:border-white/10 bg-white p-0.5'>
                                        <AvatarImage
                                            src={item.logo?.sourceUrl || ''}
                                            alt={displayName}
                                            className='object-contain'
                                        />
                                        <AvatarFallback className='text-xs font-bold'>
                                            {generateAvatarFallback(displayName)}
                                        </AvatarFallback>
                                    </Avatar>
                                    <div className='flex flex-col overflow-hidden text-right'>
                                        <div className='flex items-center gap-1.5'>
                                            <span className='truncate font-medium max-w-[150px] sm:max-w-[200px]'>
                                                {displayName}
                                            </span>
                                            {item.sponsored ? (
                                                <div className='w-3.5 shrink-0'>
                                                    <GoldCheck className='w-full h-full' />
                                                </div>
                                            ) : item.verified || item.badge ? (
                                                <div className='w-3.5 shrink-0'>
                                                    <BlueCheck className='w-full h-full' />
                                                </div>
                                            ) : null}
                                        </div>
                                        {item.enName && (
                                            <span className='text-[10px] font-mono text-gray-400 dark:text-gray-500 font-normal truncate max-w-[160px]'>
                                                {item.enName}
                                            </span>
                                        )}
                                    </div>
                                </div>

                                {item.badge && (
                                    <span className='text-[10px] py-0.5 px-2 rounded-full bg-primary/5 dark:bg-white/10 text-primary/70 dark:text-white/70 font-yekanBakh shrink-0'>
                                        {item.badge}
                                    </span>
                                )}
                            </Link>
                        );
                    })}

                    {isLoading && (
                        <div className='py-8 text-center'>
                            <LoadingSpinner className='mx-auto size-5 animate-spin text-blue-500' />
                        </div>
                    )}

                    {!isLoading && displayedBrokers.length === 0 && (
                        <div className='py-16 text-center text-xs text-gray-400 dark:text-gray-500'>
                            هیچ موردی یافت نشد.
                        </div>
                    )}

                    {error && !isLoading && (
                        <div className='py-6 text-center text-xs text-red-500'>{error}</div>
                    )}
                </div>
            </div>
        </div>
    );

    return createPortal(modalMarkup, document.body);
};

export default PlatformSearchModal;
